import { HistoryLeftBar } from './HistoryLeftBar.component';
import { useGetFormHistoryByIdQuery } from 'src/services/api/api-client/FormsQuery';
import { Loading } from 'uikit/suspense/Loading';
import { VersionContainer } from './VersionContainer.component';
import { useMemo, useState } from 'react';
import { FormChangesDto, FormChangingActionType } from 'src/services/api/api-client';
import Styles from './HistoryTab.module.scss';
import { HistoryTabProvider } from './provider/HistoryTab.provider';
import { FormVersionFilterExpression, getPreviousVersionOfSelectedVersion } from './historyHelper';
import { useTranslation } from 'react-i18next';
import { ReactComponent as Placeholder } from 'src/assets/img/common/no_changes_placeholder.svg';

export type HistoryTabProps = {
  formResultId: number;
  hasEcrf?: boolean;
};

export type SelectedVersion = {
  formResultId: number;
  versionNumber?: number;
  minorVersionNumber?: number;
  actionType?: FormChangingActionType;
};

export const HistoryTab = ({ formResultId, hasEcrf }: HistoryTabProps) => {
  const { data: formHistory, isInitialLoading: isFormHistoryLoading } = useGetFormHistoryByIdQuery(formResultId);
  const { t } = useTranslation();

  const [selectedVersion, setSelectedVersion] = useState<FormChangesDto | undefined>();

  const selectedCard = useMemo(
    () => ({
      formResultId: formResultId,
      versionNumber: selectedVersion?.versionNumber,
      minorVersionNumber: selectedVersion?.minorVersionNumber,
      actionType: selectedVersion?.formChangingActionType,
    }),
    [
      formResultId,
      selectedVersion?.formChangingActionType,
      selectedVersion?.minorVersionNumber,
      selectedVersion?.versionNumber,
    ],
  );

  const prevVersionOfSelectedVersion = getPreviousVersionOfSelectedVersion(formHistory?.formChanges, selectedVersion);
  const filteredVersions = formHistory?.formChanges.filter(FormVersionFilterExpression);

  const placeholder = useMemo(() => {
    return (
      <div className={Styles.placeholder} data-test-id={'placeholder'}>
        <Placeholder />
        <div className={Styles.placeholderText}>{t('Forms.HistoryOfChanges.Placeholder')}</div>
      </div>
    );
  }, [t]);

  if (!filteredVersions?.length) {
    return placeholder;
  }

  return (
    <Loading
      loading={isFormHistoryLoading}
      renderChildren={false}
      containerClassName={Styles.loader}
      testId={'history-tab'}
    >
      <HistoryTabProvider
        selectedVersion={selectedVersion}
        previousVersionOfSelectedVersion={prevVersionOfSelectedVersion}
      >
        <div className={Styles.historyTab}>
          <HistoryLeftBar
            versions={filteredVersions!}
            hasEcrf={hasEcrf}
            setSelectedVersion={setSelectedVersion}
            selectedVersion={selectedCard}
          />
          <VersionContainer
            currentVersion={selectedVersion ?? filteredVersions[0]}
            previousVersion={prevVersionOfSelectedVersion}
            hasEcrf={hasEcrf}
            formResultId={formResultId}
            selectedCard={selectedCard}
          />
        </div>
      </HistoryTabProvider>
    </Loading>
  );
};
